import { useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { colors } from "@rubi/ui";

import { IUser } from "@/types/typesAlerts";

// This component is shared across RubiREPORT and RubiREGISTRY with different user lists
// So we only keep the fields actually rendered/used
interface AssignableUser {
  id: string;
  first_name: string;
  last_name: string;
  badge_number?: string | null;
  rank_short?: string | null;
}

const AssignedToSelector = ({
  currentAssignee,
  onAssign,
  userOptions,
  currentUser,
  disableEdit,
}: {
  currentAssignee: AssignableUser | null;
  onAssign: (user_id: string | null) => void;
  userOptions: AssignableUser[];
  currentUser: IUser | null;
  disableEdit?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AssignableUser | null>(
    currentAssignee ? currentAssignee : null,
  );

  const handleChange = (event, newValue) => {
    setSelectedUser(newValue);
    onAssign(newValue?.id ?? null);

    if (!newValue) {
      setIsEditing(false);
    }
  };

  const handleAssignToMe = () => {
    setSelectedUser(currentUser ? currentUser : null);
    onAssign(currentUser?.id ?? null);
    setIsEditing(false);
  };

  if (disableEdit && !currentAssignee) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "2px",
      }}
    >
      {!isEditing && currentAssignee ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: colors.action.hover,
            padding: "4px 10px",
            borderRadius: "16px",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "500" }}>
            {currentAssignee?.first_name} {currentAssignee?.last_name}
          </Typography>
          {!disableEdit && (
            <Tooltip title="Change Assignee">
              <IconButton
                size="small"
                onClick={() => setIsEditing(true)}
                data-testid="change-assignee-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Autocomplete
            options={userOptions}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name}`
            }
            value={selectedUser}
            renderOption={(props, option, { inputValue }) => {
              const matchIndex = `${option.first_name} ${option.last_name}`
                .toLowerCase()
                .indexOf(inputValue.toLowerCase());

              let label;
              if (matchIndex >= 0) {
                const beforeMatch =
                  `${option.first_name} ${option.last_name}`.slice(
                    0,
                    matchIndex,
                  );
                const matchText =
                  `${option.first_name} ${option.last_name}`.slice(
                    matchIndex,
                    matchIndex + inputValue.length,
                  );
                const afterMatch =
                  `${option.first_name} ${option.last_name}`.slice(
                    matchIndex + inputValue.length,
                  );
                label = (
                  <span>
                    {beforeMatch}
                    <b>{matchText}</b>
                    {afterMatch}
                  </span>
                );
              } else {
                label = `${option.first_name} ${option.last_name}`;
              }

              return (
                <li {...props} key={option.id}>
                  {label}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Assign to a user..."
                variant="outlined"
                size="small"
                sx={{
                  width: 180,
                  fontSize: "13px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    fontSize: "13px",
                    height: "32px",
                    "& fieldset": {
                      borderColor: "action.disabled",
                    },
                    "&:hover fieldset": {
                      borderColor: "text.secondary",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary.main,
                    },
                  },
                }}
              />
            )}
            onChange={handleChange}
            onBlur={() => setIsEditing(false)}
          />
          <Button
            variant="text"
            onClick={handleAssignToMe}
            sx={{
              fontSize: "11px",
              padding: "0",
              margin: "0",
              textTransform: "none",
              color: colors.error.main,
              alignSelf: "flex-start",
              minWidth: "auto",
              lineHeight: 1,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Assign to myself
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AssignedToSelector;
